import React, { useMemo, useState } from 'react';
import TransactionVolumeChart from '../../dashboard/components/TransactionVolumeChart';
import {
    DEFAULT_DAILY_TIMELINE_DAYS,
    dailyPresetLabel,
} from '../../../utils/chartTimeline';
import WorkActivityCalendar from './WorkActivityCalendar';
import {
    buildActivityCalendar,
    buildDailyPayoutChartData,
    computeDailyStatsSummary,
} from '../utils/workStatsMetrics';

const StatCard = ({ label, value, hint }) => (
    <div className="rounded-xl border border-zinc-200 bg-zinc-50/80 p-4 dark:border-zinc-800 dark:bg-zinc-900/40">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">{label}</p>
        <p className="mt-2 text-2xl font-bold tabular-nums text-zinc-900 dark:text-white">{value}</p>
        {hint ? <p className="mt-1 text-xs text-zinc-500">{hint}</p> : null}
    </div>
);

const JobsStatsPage = ({ role = 'employee', tasks = [], tokenSymbol = 'USDC', formatNumber }) => {
    const [timelineDays, setTimelineDays] = useState(DEFAULT_DAILY_TIMELINE_DAYS);
    const periodLabel = dailyPresetLabel(timelineDays);
    const calendarWeeks = Math.max(5, Math.ceil(timelineDays / 7));

    const calendar = useMemo(
        () => buildActivityCalendar(tasks, role, calendarWeeks),
        [tasks, role, calendarWeeks],
    );

    const chartData = useMemo(
        () => buildDailyPayoutChartData(tasks, role, timelineDays),
        [tasks, role, timelineDays],
    );

    const summary = useMemo(
        () => computeDailyStatsSummary(tasks, role, timelineDays),
        [tasks, role, timelineDays],
    );

    const formatAmt = (n) => `${formatNumber(n)} ${tokenSymbol}`;

    const isEmployee = role === 'employee';
    const earningsLabel = isEmployee ? 'Earned' : 'Paid out';

    return (
        <div className="w-full space-y-6 pb-10">
            <div>
                <h1 className="text-2xl font-bold tracking-tight text-zinc-900 dark:text-white">Stats</h1>
                <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
                    {isEmployee
                        ? 'Your work rhythm and earnings for the selected period.'
                        : 'Team payouts and task activity for the selected period.'}
                </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
                <StatCard
                    label={isEmployee ? 'Earned (period)' : 'Paid out (period)'}
                    value={formatAmt(summary.totalEarned)}
                    hint="Released tasks in range"
                />
                <StatCard
                    label="Active days"
                    value={summary.activeDays}
                    hint="Days with logged activity"
                />
                <StatCard
                    label="Submissions"
                    value={summary.submissions}
                    hint="Work packages submitted"
                />
            </div>

            <section className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-950/50">
                <h2 className="text-sm font-semibold text-zinc-900 dark:text-white">Work activity</h2>
                <p className="mt-0.5 text-xs text-zinc-500">GitHub-style calendar · {periodLabel}</p>
                <div className="mt-4">
                    <WorkActivityCalendar calendar={calendar} role={role} />
                </div>
            </section>

            <TransactionVolumeChart
                data={chartData}
                title={isEmployee ? 'Earnings' : 'Payouts'}
                subtitle={`Daily ${isEmployee ? 'amount received' : 'amount released to team'} · ${periodLabel}`}
                secondaryLabel={earningsLabel}
                trendTabLabel="Trend"
                showSentSeries={false}
                enableCompare={false}
                enableTimeline
                timelineMode="days"
                timelineDays={timelineDays}
                onTimelineChange={(days) => setTimelineDays(days)}
                valueFormat="currency"
            />
        </div>
    );
};

export default JobsStatsPage;
